import { useCallback, useRef } from 'react'
import { flushSync } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { usePostHog } from 'posthog-js/react'
import {
  Plus,
  History,
  ChevronLeft,
  PanelRight,
  PanelRightOpen,
} from 'lucide-react'

import { SidebarHeader, useSidebar } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useConversationContext } from '@/contexts/ConversationContext'
import { useConversationNav } from '@/hooks/useConversationNav'
import { useConversationsList } from '@/hooks/useConversationsQuery'
import { AgentAvatar } from '@/components/AgentChat/AgentAvatar'

const ICON_BUTTON_CLASS =
  'size-8 rounded-lg bg-transparent text-foreground hover:bg-black/5 active:bg-black/10 focus-visible:ring-2 focus-visible:ring-primary/30 [&_svg]:size-4.5'

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export function ConversationSidebarHeader() {
  const { open, openMobile, isMobile, setOpen, setOpenMobile } = useSidebar()
  const mobileOpen = isMobile && openMobile
  const isOpen = open || mobileOpen
  const { agentId, streamingState } = useConversationContext()
  const { goToNewChat, goToHistory, goToConversation, clearConversation } =
    useConversationNav()
  const [searchParams] = useSearchParams()
  const posthog = usePostHog()
  // Focus target for the collapse/expand morph (collapse toggle when expanded,
  // avatar button when collapsed — only one mounts at a time).
  const toggleRef = useRef<HTMLButtonElement>(null)

  const viewParam = searchParams.get('view')
  const conversationId = searchParams.get('conversationId') || undefined
  const isHistoryMode = viewParam === 'history'

  const { data: conversations } = useConversationsList(agentId)

  let title = 'New chat'
  if (isHistoryMode) {
    title = 'Chat history'
  } else if (conversationId) {
    title =
      conversations?.find((c) => c.id === conversationId)?.title || 'New chat'
  }

  const handleNewConversation = useCallback(() => {
    goToNewChat(agentId)
    posthog?.capture('conversation_new', { agent: agentId })
  }, [agentId, goToNewChat, posthog])

  const handleHistoryClick = useCallback(() => {
    goToHistory(agentId)
  }, [agentId, goToHistory])

  const handleBackFromHistory = useCallback(() => {
    if (conversationId) {
      goToConversation(agentId, conversationId)
    } else {
      clearConversation(agentId)
    }
  }, [agentId, conversationId, goToConversation, clearConversation])

  // Collapse/expand the chat panel. The agent avatar persists across both
  // states — left of the title when expanded, alone in the rail when
  // collapsed — and morphs between the two via a View Transition (shared
  // `view-transition-name`). Falls back to an instant toggle when the API is
  // unavailable or the user prefers reduced motion.
  const toggleSidebar = useCallback(
    (next: boolean) => {
      if (isMobile) {
        setOpenMobile(next)
        return
      }
      if (!document.startViewTransition || prefersReducedMotion()) {
        setOpen(next)
        return
      }
      document
        .startViewTransition(() => flushSync(() => setOpen(next)))
        .finished.finally(() => toggleRef.current?.focus())
        // A rapid re-toggle can abort the in-flight transition; ignore.
        .catch(() => {})
    },
    [isMobile, setOpen, setOpenMobile]
  )

  // Closed/rail state — avatar sits at the top of the rail (morphing from its
  // expanded position) with a dedicated expand toggle stacked below it.
  if (!isOpen) {
    return (
      <SidebarHeader className="flex-col">
        {/* h-12 matches the open header so the morphing avatar's center holds */}
        <div className="flex h-12 w-full items-center justify-center">
          <AgentAvatar
            agentId={agentId}
            className="[view-transition-name:agent-avatar]"
          />
        </div>
        {!isMobile && (
          <div className="flex w-full items-center justify-center py-3">
            <Button
              ref={toggleRef}
              variant="ghost"
              size="icon"
              onClick={() => toggleSidebar(true)}
              className={ICON_BUTTON_CLASS}
              aria-label="Show chat"
              title="Show chat">
              <PanelRightOpen />
            </Button>
          </div>
        )}
      </SidebarHeader>
    )
  }

  return (
    <SidebarHeader className="h-12 px-4 py-3">
      <div className="flex items-center gap-2 w-full">
        {isHistoryMode ? (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackFromHistory}
              className={ICON_BUTTON_CLASS}
              aria-label="Back to chat">
              <ChevronLeft />
            </Button>
            <span className="text-sm font-medium text-foreground">
              Chat history
            </span>
          </>
        ) : (
          <>
            <AgentAvatar
              agentId={agentId}
              className="[view-transition-name:agent-avatar]"
            />
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex-1 min-w-0 truncate text-sm font-medium text-foreground cursor-default">
                    {title}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{title}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNewConversation}
                disabled={streamingState?.isStreaming}
                className={cn(
                  ICON_BUTTON_CLASS,
                  streamingState?.isStreaming && 'opacity-60'
                )}
                aria-label="New conversation">
                <Plus />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleHistoryClick}
                className={ICON_BUTTON_CLASS}
                aria-label="Conversations history">
                <History />
              </Button>
              {!isMobile && (
                <Button
                  ref={toggleRef}
                  variant="ghost"
                  size="icon"
                  onClick={() => toggleSidebar(false)}
                  className={ICON_BUTTON_CLASS}
                  aria-label="Hide chat"
                  title="Hide Chat">
                  <PanelRight />
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </SidebarHeader>
  )
}
